import React, { useCallback, useEffect, useRef } from "react";

export type SplitOrientation = "horizontal" | "vertical";

interface Props {
  /** Pane rendered first — above the divider, or to its left. */
  first: React.ReactNode;
  /** Pane rendered second — below the divider, or to its right. */
  second: React.ReactNode;
  /**
   * "horizontal" gives a horizontal divider with the panes stacked;
   * "vertical" gives a vertical divider with the panes side by side.
   * Named for the divider, matching the ARIA `separator` orientation.
   */
  orientation: SplitOrientation;
  /** Share of the container given to the first pane, 0-100. */
  ratio: number;
  /** Called with the new ratio as the divider is dragged. Values are raw; the caller clamps. */
  onRatioChange: (ratio: number) => void;
  /** Step applied per arrow keypress when the divider has focus. */
  keyboardStep?: number;
}

const DIVIDER_THICKNESS = 8;

/**
 * Two resizable panes with a draggable divider between them.
 *
 * Hand-rolled rather than pulled in: antd 4.24 predates `Splitter` (added in
 * antd 5.21), and this needs no new dependency.
 *
 * Both panes are given `minHeight: 0` / `minWidth: 0`. Without it a flex child
 * refuses to shrink below its content size, so the grid pane would keep its
 * natural height and push the terminal pane out of view instead of sharing the
 * container — the default `min-*: auto` on flex items is the whole reason this
 * looks like it "doesn't resize".
 */
export default function TerminalSplitLayout({
  first,
  second,
  orientation,
  ratio,
  onRatioChange,
  keyboardStep = 5,
}: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const draggingRef = useRef(false);
  const isVertical = orientation === "vertical";

  const ratioFromEvent = useCallback(
    (clientX: number, clientY: number): number | null => {
      const el = containerRef.current;
      if (!el) return null;
      const rect = el.getBoundingClientRect();
      const span = isVertical ? rect.width : rect.height;
      if (span <= 0) return null;
      const offset = isVertical ? clientX - rect.left : clientY - rect.top;
      const next = (offset / span) * 100;
      // A pointer event without usable coordinates yields NaN, and NaN would
      // travel straight into the stored ratio and blank both panes — Math.min
      // and Math.max propagate it rather than clamping it away.
      return Number.isFinite(next) ? next : null;
    },
    [isVertical],
  );

  // Listeners live on window, not the divider: once a drag starts the pointer
  // routinely leaves the 8px divider, and a listener bound to the element
  // would stop receiving moves the moment it did.
  useEffect(() => {
    const onPointerMove = (event: PointerEvent) => {
      if (!draggingRef.current) return;
      const next = ratioFromEvent(event.clientX, event.clientY);
      if (next === null) return;
      // Suppress text selection across the page while dragging.
      event.preventDefault();
      onRatioChange(next);
    };
    const onPointerUp = () => {
      draggingRef.current = false;
    };
    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", onPointerUp);
    window.addEventListener("pointercancel", onPointerUp);
    return () => {
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", onPointerUp);
      window.removeEventListener("pointercancel", onPointerUp);
    };
  }, [ratioFromEvent, onRatioChange]);

  const onKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    const decreaseKey = isVertical ? "ArrowLeft" : "ArrowUp";
    const increaseKey = isVertical ? "ArrowRight" : "ArrowDown";
    if (event.key === decreaseKey) {
      event.preventDefault();
      onRatioChange(ratio - keyboardStep);
    } else if (event.key === increaseKey) {
      event.preventDefault();
      onRatioChange(ratio + keyboardStep);
    }
  };

  const paneBase: React.CSSProperties = {
    overflow: "auto",
    minHeight: 0,
    minWidth: 0,
  };

  return (
    <div
      ref={containerRef}
      data-testid="terminal-split-layout"
      style={{
        display: "flex",
        flexDirection: isVertical ? "row" : "column",
        width: "100%",
        // Fill the viewport below the monitoring header so the two panes have
        // a bounded box to divide. Without a definite size the percentages
        // resolve against content height and neither pane scrolls.
        height: "calc(100vh - 160px)",
      }}
    >
      <div style={{ ...paneBase, flexBasis: `${ratio}%`, flexGrow: 0, flexShrink: 0 }}>{first}</div>
      {/*
       * A native <button> carrying role="slider".
       *
       * WAI-ARIA's window-splitter pattern is a FOCUSABLE separator, which
       * behaves — and is announced — as a slider over the pane ratio, so the
       * role is accurate rather than a workaround. Using a real button rather
       * than a div with tabIndex means focus, Enter/Space and the focus ring
       * come from the platform instead of being re-implemented, and it keeps
       * the element interactive by construction (jsx-a11y rejects keyboard and
       * pointer handlers on non-interactive elements, and it is right to).
       */}
      <button
        type="button"
        data-testid="terminal-split-divider"
        role="slider"
        aria-orientation={orientation}
        aria-valuenow={Math.round(ratio)}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label="Resize terminal and dashboard panes"
        onKeyDown={onKeyDown}
        onPointerDown={(event) => {
          draggingRef.current = true;
          // Capture so the divider keeps receiving events even if the pointer
          // ends up over an iframe or a child that stops propagation.
          event.currentTarget.setPointerCapture?.(event.pointerId);
        }}
        style={{
          flex: `0 0 ${DIVIDER_THICKNESS}px`,
          cursor: isVertical ? "col-resize" : "row-resize",
          background: "hsl(217, 84%, 52%, 25%)",
          border: "none",
          padding: 0,
          borderRadius: 4,
          touchAction: "none",
        }}
      />
      <div style={{ ...paneBase, flex: "1 1 0%" }}>{second}</div>
    </div>
  );
}
